// scopes for calendar related query keys

import type { QueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useContinuousWorkPlansForUsers } from "./calendar.queries";
const DATE_FORMAT = "YYYY-MM-DD";

const scopes = {
  usersByLocation: "usersByLocation",
  usersByRoleAndDate: "usersByRoleAndDate",
  appointmentsForDate: "appointmentsForDate",
  appointmentsForDateRange: "appointmentsForDateRange",
  // continuousAppointmentsForDate: "continuousAppointmentsForDate",
  unscheduledPatientAppointments: "unscheduledPatientAppointments",
  unscheduledContinuousAppointments: "unscheduledContinuousAppointments",
  continuousAppointmentsForPatient: "continuousAppointmentsForPatient",
  continuousAppointmentsForUser: "continuousAppointmentsForUser",
  continuousAppointmentsByDay: "continuousAppointmentsByDay",
  patientUnavailabilityForCalendar: "patientUnavailabilityForCalendar",
  patientUnavailabilityTypeByDateForCalendar: "patientUnavailabilityTypeByDateForCalendar",
  derivedPatientBlockedTimes: "derivedPatientBlockedTimes",
  sublocalitiesForAppointments: "sublocalitiesForAppointments",
  workingHours: "workingHours",
  deltaSummary: "deltaSummary",
  continuousWorkPlansForUsers: "continuousWorkPlansForUsers",
  locationAbsences: "locationAbsences",
};

export const calendarKeys = {
  usersByRoleAndDate: {
    scope: scopes.usersByRoleAndDate,
    all: () => [scopes.usersByRoleAndDate] as const,
    detail: (locationId: string, date: string, role: string = 'therapist') =>
      [scopes.usersByRoleAndDate, { locationId, date, role }] as const,
  },
  usersByLocation: {
    scope: scopes.usersByLocation,
    // All users for a location (optionally including others) with a user id filter
    // userIds is sorted to keep the query key stable
    all: () => [scopes.usersByLocation] as const,
    detail: (locationId: string, includeOtherUsers?: boolean, userIds?: (string | undefined)[]) =>
      [
        scopes.usersByLocation,
        {
          locationId,
          includeOtherUsers: !!includeOtherUsers,
          userIds: (userIds || []).filter(Boolean).slice().sort(),
        },
      ] as const,
  },
  appointmentsForDate: {
    scope: scopes.appointmentsForDate,
    all: (): [unknown] => [scopes.appointmentsForDate],
    detail: (date: Date, userIds?: string[]) =>
      userIds
        ? ([
            scopes.appointmentsForDate,
            { date: moment(date).format(DATE_FORMAT), userIds },
          ] as const)
        : ([scopes.appointmentsForDate, { date: moment(date).format(DATE_FORMAT) }] as const),
  },
  appointmentsForDateRange: {
    scope: scopes.appointmentsForDateRange,
    all: (): [unknown] => [scopes.appointmentsForDateRange],
    detail: (userId: string, startDate: string, endDate: string) =>
      [scopes.appointmentsForDateRange, { userId, startDate, endDate }] as const,
  },
  /*
  continuousAppointmentsForDate: {
    scope: scopes.continuousAppointmentsForDate,
    all: () => [scopes.continuousAppointmentsForDate] as const,
    detail: (date: Date) => [scopes.continuousAppointmentsForDate, { day: date.getDay() }] as const,
  },*/
  continuousAppointmentsByDay: {
    scope: scopes.continuousAppointmentsByDay,
    all: (): [unknown] => [scopes.continuousAppointmentsByDay] as const,
    detail: (locationId: string, dayOfWeek: number) =>
      [scopes.continuousAppointmentsByDay, { locationId, dayOfWeek }] as const,
  },
  unscheduledPatientAppointments: {
    scope: scopes.unscheduledPatientAppointments,
    all: () => [scopes.unscheduledPatientAppointments] as const,
    detail: (patientId: number) => [scopes.unscheduledPatientAppointments, { patientId }] as const,
  },
  unscheduledContinuousAppointments: {
    scope: scopes.unscheduledContinuousAppointments,
    all: () => [scopes.unscheduledContinuousAppointments] as const,
    detail: (patientId: number) =>
      [scopes.unscheduledContinuousAppointments, { patientId }] as const,
  },
  continuousAppointmentsForPatient: {
    scope: scopes.continuousAppointmentsForPatient,
    all: () => [scopes.continuousAppointmentsForPatient] as const,
    detail: (patientId: number) =>
      [scopes.continuousAppointmentsForPatient, { patientId }] as const,
  },
  continuousAppointmentsForUser: {
    scope: scopes.continuousAppointmentsForUser,
    all: () => [scopes.continuousAppointmentsForUser] as const,
    detail: (userId: string) =>
      [scopes.continuousAppointmentsForUser, { userId }] as const,
  },
  patientUnavailabilityForCalendar: {
    scope: scopes.patientUnavailabilityForCalendar,
    all: () => [scopes.patientUnavailabilityForCalendar] as const,
    detail: (patientId: number, date: string) =>
      [scopes.patientUnavailabilityForCalendar, { patientId, date }] as const,
  },
  patientUnavailabilityTypeByDateForCalendar: {
    scope: scopes.patientUnavailabilityTypeByDateForCalendar,
    all: () => [scopes.patientUnavailabilityTypeByDateForCalendar] as const,
    detail: (patientId: number, date: string) =>
      [scopes.patientUnavailabilityTypeByDateForCalendar, { patientId, date }] as const,
  },
  derivedPatientBlockedTimes: {
    scope: scopes.derivedPatientBlockedTimes,
    all: () => [scopes.derivedPatientBlockedTimes] as const,
    detail: (patientId: number, startDate: string, endDate: string) =>
      [scopes.derivedPatientBlockedTimes, { patientId, startDate, endDate }] as const,
  },
  sublocalitiesForAppointments: {
    scope: scopes.sublocalitiesForAppointments,
    all: (locationId?: string) => [scopes.sublocalitiesForAppointments, { locationId }] as const,
    detail: ( addressIds: (string | undefined)[], locationId?: string,) =>
      [
        scopes.sublocalitiesForAppointments,
        { locationId, addressIds: (addressIds || []).filter(Boolean).slice().sort() },
      ] as const,
  },
  workingHours: {
    scope: scopes.workingHours,
    all: () => [scopes.workingHours] as const,
    // locationId is part of the key: work plans are location-specific, so the
    // same user list yields different hours per displayed location.
    detail: (userIds: string[], dateString: string, locationId?: string) =>
      [scopes.workingHours, { userIds, dateString, locationId }] as const,
  },
  deltaSummary: {
    scope: scopes.deltaSummary,
    all: () => [scopes.deltaSummary] as const,
    detail: (userIds: string[], startDate: string, endDate: string) =>
      [scopes.deltaSummary, { userIds, startDate, endDate }] as const,
  },
  continuousWorkPlansForUsers: {
    scope: scopes.continuousWorkPlansForUsers,
    all: () => [scopes.continuousWorkPlansForUsers] as const,
    detail: (userIds: string[], dayOfWeek: number | undefined, dateString: string, locationId?: string) =>
      [scopes.continuousWorkPlansForUsers, { userIds, dayOfWeek, dateString, locationId }] as const,
  },
  locationAbsences: {
    scope: scopes.locationAbsences,
    all: () => [scopes.locationAbsences] as const,
    detail: (locationId: string) => [scopes.locationAbsences, { locationId }] as const,
  },
};

/**
 * Invalidate BOTH calendar appointment caches at once.
 *
 * The main calendar reads from the per-day cache (`appointmentsForDate`) while
 * the single-therapist view reads from the date-range cache
 * (`appointmentsForDateRange`). They are independent React Query scopes, so an
 * invalidation of one does not touch the other. As a rule, anything that needs
 * to refetch the main calendar must also refetch the single-user view, so always
 * invalidate them together via this helper instead of invalidating
 * `appointmentsForDate` on its own.
 *
 * Invalidating an inactive query is a safe no-op (React Query only refetches
 * active queries by default), so calling this from a context where only one of
 * the two views is mounted is behaviour-preserving.
 *
 * NOTE: This is for *invalidations* (refetch triggers) only. Do NOT use it to
 * mirror in-place optimistic patches (`setQueriesData`/`cancelQueries`) — the
 * two caches have different shapes and the range cache has its own patch logic.
 */
export const invalidateCalendarAppointmentCaches = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: calendarKeys.appointmentsForDate.all() }),
    queryClient.invalidateQueries({ queryKey: calendarKeys.appointmentsForDateRange.all() }),
  ]);

/**
 * Invalidate EVERYTHING the calendar board is derived from — the "data may be
 * outdated" big hammer behind the toolbar refresh button and socket fallback.
 * Extracted from CalendarController.invalidateCalendarQueries so hosts without
 * a controller instance (e.g. the MobiliCalendar header toolbar) trigger the
 * exact same refresh; keep the two in sync by keeping only this one.
 */
export const invalidateAllCalendarQueries = (queryClient: QueryClient) =>
  Promise.all([
    // Users
    queryClient.invalidateQueries({ queryKey: calendarKeys.usersByLocation.all() }),
    queryClient.invalidateQueries({ queryKey: calendarKeys.usersByRoleAndDate.all() }),
    // Appointments (main day cache + single-therapist date-range cache)
    invalidateCalendarAppointmentCaches(queryClient),
    queryClient.invalidateQueries({ queryKey: calendarKeys.continuousAppointmentsByDay.all() }),
    queryClient.invalidateQueries({ queryKey: calendarKeys.continuousAppointmentsForPatient.all() }),
    queryClient.invalidateQueries({ queryKey: calendarKeys.continuousAppointmentsForUser.all() }),
    // Unscheduled
    queryClient.invalidateQueries({ queryKey: calendarKeys.unscheduledPatientAppointments.all() }),
    queryClient.invalidateQueries({ queryKey: calendarKeys.unscheduledContinuousAppointments.all() }),
    // Patient availability
    queryClient.invalidateQueries({ queryKey: calendarKeys.patientUnavailabilityForCalendar.all() }),
    queryClient.invalidateQueries({
      queryKey: calendarKeys.patientUnavailabilityTypeByDateForCalendar.all(),
    }),
    // Working hours & sublocalities
    queryClient.invalidateQueries({ queryKey: calendarKeys.workingHours.all() }),
    queryClient.invalidateQueries({ queryKey: calendarKeys.continuousWorkPlansForUsers.all() }),
    queryClient.invalidateQueries({ queryKey: calendarKeys.sublocalitiesForAppointments.all() }),
  ]);
